/**
 * ImageDropzone.tsx
 *
 * A storage-agnostic image input component supporting paste, drag-drop, and file picker.
 * Emits raw File objects via callbacks - parent handles storage integration.
 */

// 1. External dependencies
import React, { useRef, useState, useCallback } from 'react';
import { ImagePlus, Trash2, RefreshCw, AlertCircle } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Spinner } from '@enfomentis/ui';

// =============================================================================
// Constants
// =============================================================================

/**
 * Accepted MIME types for image upload.
 * NOTE: This list mirrors ImageStorageService ALLOWED_MIME_TYPES to prevent drift.
 * If updating, also update ImageStorageService.
 */
const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Accept attribute string for file input */
const ACCEPT_ATTRIBUTE = ACCEPTED_MIME_TYPES.join(',');

// =============================================================================
// Types
// =============================================================================

/**
 * Props for ImageDropzone component
 */
export interface ImageDropzoneProps {
  /** Object URL or data URL for thumbnail preview */
  previewUrl: string | null;
  /** Callback when user selects/pastes/drops a valid image */
  onFileSelected: (file: File) => void;
  /** Callback when user clears the image */
  onClear: () => void;
  /** Callback when user clicks preview (for lightbox) */
  onPreviewClick?: (() => void) | undefined;
  /** Callback when validation error occurs (e.g., unsupported file type) */
  onError?: ((message: string) => void) | undefined;
  /** Callback when image element fails to load (stale URL) */
  onImageError?: (() => void) | undefined;
  /** Error message to display */
  error?: string | null | undefined;
  /** Callback for retry button (shown when error is set) */
  onRetry?: (() => void) | undefined;
  /** Shows loading state during upload */
  isLoading?: boolean | undefined;
  /** Shows loading state during remote blob fetch */
  isFetchingRemote?: boolean | undefined;
  /** Field label (passed through from field config) */
  label?: string | undefined;
  /** Field description/helper text */
  description?: string | undefined;
  /** Disables all interactions */
  disabled?: boolean | undefined;
  /** Whether an image reference exists (even if not loaded) - affects error state display */
  hasExistingImage?: boolean | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a MIME type is in the accepted list
 */
const isAcceptedMimeType = (mimeType: string): boolean => {
  return ACCEPTED_MIME_TYPES.includes(mimeType as (typeof ACCEPTED_MIME_TYPES)[number]);
};

/**
 * Find the first valid image file from a FileList or DataTransferItemList
 */
const findFirstValidImage = (
  files: FileList | DataTransferItemList
): File | null => {
  for (let i = 0; i < files.length; i++) {
    const item = files[i];
    if (!item) continue;

    // Handle DataTransferItem
    if ('kind' in item) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file && isAcceptedMimeType(file.type)) {
          return file;
        }
      }
    } else {
      // Handle File directly
      if (isAcceptedMimeType(item.type)) {
        return item;
      }
    }
  }
  return null;
};

/**
 * Check if any file in the list is an image (even if unsupported type)
 */
const hasAnyImageFile = (files: FileList | DataTransferItemList): boolean => {
  for (let i = 0; i < files.length; i++) {
    const item = files[i];
    if (!item) continue;

    if ('kind' in item) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        return true;
      }
    } else if (item.type.startsWith('image/')) {
      return true;
    }
  }
  return false;
};

// =============================================================================
// Component
// =============================================================================

/**
 * ImageDropzone Component
 *
 * Provides image input via paste, drag-drop, and file picker.
 * Storage-agnostic - emits File objects via callbacks.
 */
export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  previewUrl,
  onFileSelected,
  onClear,
  onPreviewClick,
  onError,
  onImageError,
  error,
  onRetry,
  isLoading = false,
  isFetchingRemote = false,
  label,
  description,
  disabled = false,
  hasExistingImage = false,
}) => {
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // State
  const [dragEnterCounter, setDragEnterCounter] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  // Derived state
  const isDragOver = dragEnterCounter > 0;
  const isInteractionDisabled = isLoading || isFetchingRemote || disabled;

  // Show inline error state when there's an error for an existing image that failed to load
  const showInlineErrorState = !!(error && hasExistingImage && !previewUrl);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Trigger file picker
   */
  const triggerFilePicker = useCallback((): void => {
    if (isInteractionDisabled) return;
    fileInputRef.current?.click();
  }, [isInteractionDisabled]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const validFile = findFirstValidImage(files);
      if (validFile) {
        onFileSelected(validFile);
      } else if (hasAnyImageFile(files)) {
        // Has image files but none are valid types
        onError?.('Unsupported image format. Please use PNG, JPEG, or WebP.');
      }

      // Clear input value so re-selecting same file works
      event.target.value = '';
    },
    [onFileSelected, onError]
  );

  /**
   * Handle paste event
   */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>): void => {
      if (isInteractionDisabled) return;

      const items = event.clipboardData?.items;
      if (!items) return;

      // Check if there are any image items at all
      if (!hasAnyImageFile(items)) {
        // No image items - ignore silently (don't clear error state)
        return;
      }

      const validFile = findFirstValidImage(items);
      if (validFile) {
        event.preventDefault();
        onFileSelected(validFile);
        // Blur dropzone after successful file selection
        dropzoneRef.current?.blur();
      } else {
        // Has image items but none are valid types
        event.preventDefault();
        onError?.('Unsupported image format. Please use PNG, JPEG, or WebP.');
      }
    },
    [isInteractionDisabled, onFileSelected, onError]
  );

  /**
   * Handle drag enter
   */
  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      if (isInteractionDisabled) return;
      setDragEnterCounter((prev) => prev + 1);
    },
    [isInteractionDisabled]
  );

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      setDragEnterCounter((prev) => Math.max(0, prev - 1));
    },
    []
  );

  /**
   * Handle drag over (required to enable drop)
   */
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  /**
   * Handle drop
   */
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      setDragEnterCounter(0);

      if (isInteractionDisabled) return;

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const validFile = findFirstValidImage(files);
      if (validFile) {
        onFileSelected(validFile);
        // Blur dropzone after successful file selection
        dropzoneRef.current?.blur();
      } else if (hasAnyImageFile(files)) {
        // Has image files but none are valid types
        onError?.('Unsupported image format. Please use PNG, JPEG, or WebP.');
      }
    },
    [isInteractionDisabled, onFileSelected, onError]
  );

  /**
   * Handle clear button click
   */
  const handleClear = useCallback((): void => {
    onClear();
  }, [onClear]);

  /**
   * Handle preview click
   */
  const handlePreviewClick = useCallback((): void => {
    if (onPreviewClick && !isInteractionDisabled) {
      onPreviewClick();
    }
  }, [onPreviewClick, isInteractionDisabled]);

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <span className="text-sm text-text-secondary block mb-1">{label}</span>
      )}

      {/* Description */}
      {description && (
        <span className="text-xs text-text-tertiary block mb-2">
          {description}
        </span>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        onChange={handleFileInputChange}
        className="hidden"
        aria-label={label || 'Select image file'}
        disabled={isInteractionDisabled}
      />

      {/* Main container */}
      <div
        ref={containerRef}
        onPaste={handlePaste}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="relative"
        aria-busy={isLoading || isFetchingRemote}
      >
        {previewUrl ? (
          // Preview State
          <div
            className={`
              relative rounded-lg overflow-hidden bg-default-100
              min-h-[120px] flex items-center justify-center
              ${onPreviewClick && !isInteractionDisabled ? 'cursor-pointer' : ''}
            `}
            onClick={onPreviewClick ? handlePreviewClick : undefined}
            role={onPreviewClick ? 'button' : undefined}
            aria-label={onPreviewClick ? 'View full size image' : undefined}
            tabIndex={onPreviewClick && !isInteractionDisabled ? 0 : undefined}
            onKeyDown={
              onPreviewClick && !isInteractionDisabled
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePreviewClick();
                    }
                  }
                : undefined
            }
          >
            {/* Thumbnail */}
            <img
              src={previewUrl}
              alt="Image preview"
              className="max-h-[200px] max-w-full object-contain"
              onError={onImageError}
            />

            {/* Clear button - wrapped to stop event propagation */}
            {!isInteractionDisabled && (
              <div
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="absolute top-2 right-2"
              >
                <Button
                  isIconOnly
                  variant="flat"
                  size="sm"
                  onPress={handleClear}
                  aria-label="Remove image"
                  className="bg-background/80 backdrop-blur-sm min-w-[44px] min-h-[44px]"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        ) : showInlineErrorState ? (
          // Error State for existing image that failed to load
          <div
            ref={dropzoneRef}
            tabIndex={isInteractionDisabled ? -1 : 0}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            role="alert"
            aria-label="Image failed to load"
            className={`
              min-h-[120px] p-4 rounded-lg
              border-2 border-dashed border-danger
              bg-danger/10
              flex flex-col items-center justify-center gap-3
              transition-colors duration-150
              outline-none
              ${isFocused && !isInteractionDisabled ? 'ring-2 ring-danger/50' : ''}
              ${isInteractionDisabled ? 'opacity-50' : ''}
            `}
          >
            <AlertCircle className="w-8 h-8 text-danger" />
            <p className="text-sm text-danger text-center">{error}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {onRetry && (
                <Button
                  variant="flat"
                  color="danger"
                  size="sm"
                  onPress={onRetry}
                  isDisabled={isInteractionDisabled}
                  className="min-h-[44px]"
                  startContent={<RefreshCw className="w-4 h-4" />}
                >
                  Retry
                </Button>
              )}
              <Button
                variant="light"
                size="sm"
                onPress={triggerFilePicker}
                isDisabled={isInteractionDisabled}
                className="min-h-[44px]"
              >
                Replace Image
              </Button>
            </div>
          </div>
        ) : (
          // Empty/Dropzone State
          <div
            ref={dropzoneRef}
            tabIndex={isInteractionDisabled ? -1 : 0}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label="Image upload area: paste, drag & drop, or use Browse button"
            aria-disabled={isInteractionDisabled}
            className={`
              min-h-[120px] p-4 rounded-lg
              border-2 border-dashed
              flex flex-col items-center justify-center gap-2
              transition-colors duration-150
              outline-none
              ${
                isDragOver
                  ? 'border-primary bg-primary/10'
                  : isFocused && !isInteractionDisabled
                    ? 'border-primary bg-primary/5'
                    : 'border-default-300 bg-default-100'
              }
              ${
                isInteractionDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-default'
              }
            `}
          >
            <ImagePlus
              className={`w-8 h-8 ${isDragOver ? 'text-primary' : 'text-text-tertiary'}`}
            />
            <p className="text-sm text-text-tertiary text-center">
              {isDragOver ? (
                'Drop image here'
              ) : (
                <>
                  {/* Desktop: hidden on touch devices */}
                  <span className="[@media(pointer:coarse)]:hidden" aria-hidden="true">
                    Paste, drag & drop, or
                  </span>
                  {/* Mobile: hidden on non-touch devices - no text above Browse button */}
                  {/* (paste doesn't work on mobile, so we don't mention it) */}
                  <span className="hidden [@media(pointer:coarse)]:inline" aria-hidden="true">
                    Select an image
                  </span>
                  {/* SR-only: device-appropriate text for screen readers */}
                  <span className="sr-only [@media(pointer:coarse)]:hidden">
                    Paste, drag & drop, or
                  </span>
                  <span className="sr-only hidden [@media(pointer:coarse)]:inline">
                    Select an image
                  </span>
                </>
              )}
            </p>
            <Button
              variant="flat"
              size="sm"
              onPress={triggerFilePicker}
              isDisabled={isInteractionDisabled}
              className="min-h-[44px]"
            >
              Browse
            </Button>
            <p className="text-xs text-text-tertiary">PNG, JPEG, or WebP</p>
          </div>
        )}

        {/* Loading Overlay */}
        {(isLoading || isFetchingRemote) && (
          <div
            className="absolute inset-0 bg-background/60 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center gap-2"
            aria-label={isFetchingRemote ? 'Loading image' : 'Uploading image'}
          >
            <Spinner size="lg" />
            {isFetchingRemote && (
              <span className="text-sm text-text-secondary">Loading image...</span>
            )}
          </div>
        )}
      </div>

      {/* Error Display - only show when NOT using inline error state */}
      {error && !showInlineErrorState && (
        <div
          role="alert"
          className="mt-2 p-3 rounded-lg bg-danger/10 border border-danger/20"
        >
          <p className="text-sm text-danger">{error}</p>
          {onRetry && (
            <Button
              variant="flat"
              color="danger"
              size="sm"
              onPress={onRetry}
              className="mt-2 min-h-[44px]"
              startContent={<RefreshCw className="w-4 h-4" />}
            >
              Retry
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
